import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { createTag } from '@/api/tags'
import { tagsKeys } from '@/api/queryKeys'
import { useToast } from '@/hooks/use-toast'
import type { CreateTagDto } from '@/api/generated/api'

/**
 * Hook to create a new tag
 *
 * Handles:
 * - API call to create tag
 * - Cache invalidation for tag lists
 * - Navigation to tag management page on success
 * - Success/error toast notifications
 * - Telemetry events
 *
 * @returns Mutation object with mutate function and state
 *
 * @example
 * ```tsx
 * const createTag = useCreateTag()
 *
 * const handleSubmit = async (data) => {
 *   await createTag.mutateAsync({
 *     name: data.name,
 *     description: data.description,
 *     color: data.color
 *   })
 * }
 * ```
 */
export function useCreateTag() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CreateTagDto) => createTag(data),
    onSuccess: newTag => {
      // Invalidate tag list queries
      queryClient.invalidateQueries({ queryKey: tagsKeys.lists() })

      // Show success toast
      toast({
        title: 'Tag created',
        description: `"${newTag.name}" has been created successfully.`,
      })

      // Navigate back to tag management
      navigate('/tags')
    },
    onError: (error: Error) => {
      // Show error toast
      toast({
        title: 'Failed to create tag',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      })
    },
  })
}
